import * as z from 'zod';
import { auditEntrySchema } from './audit.js';
import { backupKindSchema, backupSchema } from './backup.js';
import { strict } from './common.js';
import { credentialSchema } from './credential.js';
import { fieldDefinitionSchema } from './field.js';
import { incidentSchema } from './incident.js';
import { listSchema, pageSchema } from './pagination.js';
import { adhocFieldSchema, duplicateCandidateSchema, personDetailSchema, personImageSchema, personRevisionSchema, } from './person.js';
import { presenceEventSchema, rollCallEntrySchema } from './presence.js';
import { userSchema } from './user.js';
/**
 * The envelopes the API answers a collection with, in one place.
 *
 * **They exist as schemas because the responses exist**, and until this file they did not: a dozen
 * routes answered `{ items: [...] }` with the shape living only in a handler's return statement and
 * in whatever a test happened to assert. That is invisible until something has to describe the API
 * from the outside — the OpenAPI document, the generated client, the SPA's parser — at which point
 * every one of them is a shape somebody has to guess at, and each guess is a different guess.
 *
 * **Which envelope a collection gets is decided by whether it can grow without bound.** A page
 * carries a cursor and says "there may be more"; a list says "this is all of it". The staff of one
 * facility, the field registry, one person's photographs and one person's revisions are bounded by
 * the world rather than by a page size, so a cursor there would be a member that is always null.
 * The audit trail, the register, a key's usage and the movement log are not, so those are pages.
 *
 * **Nothing here re-declares an entity.** Every envelope wraps a schema its own module owns, so a
 * change to what a person's photograph looks like reaches this file without anybody editing it.
 */
/** `GET /api/v1/users`: the staff of one facility, which is tens of rows and never paged. */
export const userListSchema = listSchema(userSchema);
/** `GET /api/v1/fields`: the registry, retired definitions included — a read needs them to decode. */
export const fieldListSchema = listSchema(fieldDefinitionSchema);
/**
 * `POST /api/v1/fields/:id/purge`: what destroying a retired field's values actually removed.
 *
 * Counts and the field's key, which is a machine identifier rather than anything about a person.
 * It is the one response in the product that reports a deletion, so it says how much.
 */
export const fieldPurgeReportSchema = strict({
    fieldKey: z.string().min(1).max(64),
    valuesDeleted: z.int().nonnegative(),
    personsAffected: z.int().nonnegative(),
});
/**
 * `GET /api/v1/audit`: one page of the trail, newest first.
 *
 * A page rather than a list, because the trail is the one collection in this product that grows
 * without bound and is never pruned — `retention.audit_days` is `z.literal(0)`. The envelope was
 * missing until Phase 26 needed it: the route already answered a page, and the generated document
 * declared the ANSWER as the `ok|denied|error` enum, so anything built from the document — a
 * generated client, the browser's own route table, a reader of `INTEGRATION.md` — was told the
 * trail answers a string.
 */
export const auditEntryPageSchema = pageSchema(auditEntrySchema);
/**
 * `GET /api/v1/presence/incidents`: the movements that were not ordinary, or that somebody wrote
 * about, newest first over the same keyset the movement log uses.
 */
export const incidentPageSchema = pageSchema(incidentSchema);
/** `GET /api/v1/persons/:id/revisions`: one record's history, which is bounded by its edits. */
export const personRevisionListSchema = listSchema(personRevisionSchema);
/** `GET /api/v1/persons/:id/images`: active and removed rows, and never an object key. */
export const personImageListSchema = listSchema(personImageSchema);
/** `GET /api/v1/persons/:id/adhoc-fields`: the operator prose attached to one record. */
export const adhocFieldListSchema = listSchema(adhocFieldSchema);
/**
 * `PUT /api/v1/persons/:id/adhoc-fields`: the whole record back, plus the revision it wrote.
 *
 * The record rather than the fields alone, because replacing them bumps the person's `version` and
 * a client that held the old one would be refused on its next edit with nothing to explain why.
 */
export const amendAdhocFieldsResponseSchema = strict({
    person: personDetailSchema,
    revision: z.int().positive(),
});
/** `POST /api/v1/persons/duplicate-candidates`: AD-18's warning, never a constraint. */
export const duplicateCandidateListSchema = listSchema(duplicateCandidateSchema);
/** `GET /api/v1/credentials`: every pass one person has ever held, which is a handful. */
export const credentialListSchema = listSchema(credentialSchema);
/**
 * `GET /api/v1/presence/anomalies`: a bounded recent window of overrides (AD-47).
 *
 * A list rather than a page, because it is a derived query over a window rather than the movement
 * log itself — `GET /api/v1/presence/events` is the paged one, and the two answer different
 * questions on purpose.
 */
export const presenceAnomalyListSchema = listSchema(presenceEventSchema);
/** `GET /api/v1/presence/events`: the movement log, newest first, over a keyset cursor. */
export const presenceEventPageSchema = pageSchema(presenceEventSchema);
/**
 * `GET /api/v1/presence/reconciliation`: who the post-restore screen is offering to correct.
 *
 * `candidates` rather than `items`, because this is not a listing of a collection: it is a
 * PROPOSAL, and the member name is what stops a client rendering it in the same component as the
 * roll-call it is derived from.
 */
export const reconciliationCandidatesSchema = strict({
    candidates: z.array(rollCallEntrySchema),
});
/** `GET /api/v1/backups`: the archives this installation still holds. */
export const backupListSchema = listSchema(backupSchema);
/**
 * `POST /api/v1/backups`: the archive was ASKED FOR, not taken.
 *
 * A 202 and this body, because the api scrubs the master passphrase at boot (AD-14) and therefore
 * has nothing to hand the file encryptor: the worker takes the archive. `accepted` is a literal
 * `true` rather than a boolean, so a client cannot branch on a value that is never false.
 */
export const backupAcceptedSchema = strict({
    kind: backupKindSchema,
    accepted: z.literal(true),
});
//# sourceMappingURL=responses.js.map